import React, { useContext, useMemo } from 'react';
import {
    IPPObjective, IEntityFormValidations, EntityFormValidations,
    IEntityValidations, EntityValidations, ISpecificEntityFormProps, Portfolio, Contributor, CustomField,
    PPObjective,
    PPVisionObjective
} from '../../types';
import { DateService, LookupService, ReportingCycleService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrUserPicker } from '../cr/CrUserPicker';
import { CrTextField } from '../cr/CrTextField';
import { CrDropdown } from '../cr/CrDropdown';
import { EntityForm } from '../EntityForm';
import { DataContext } from '../DataContext';
import { IntegrationContext } from '../IntegrationContext';
import { OrbUserContext } from '../OrbUserContext';
import { CrChoiceGroup } from '../cr/CrChoiceGroup';
import { CrEntityPicker } from '../cr/CrEntityPicker';
import { CrDatePicker } from '../cr/CrDatePicker';
import { CrMultiDropdownWithText } from '../cr/CrMultiDropdownWithText';
import { error } from 'console';
import { CrReportingCyclePicker } from '../cr/CrReportingCyclePicker';

class ObjectiveValidations extends EntityValidations {
    public OwnerL1: string = null;
    public OwnerL2: string = null;
    public OwnerL3: string = null;
}

export const ObjectiveForm = (props: ISpecificEntityFormProps): React.ReactElement => {
    const { userPermissions, orbConfig } = useContext(OrbUserContext);
    const { disableGroupManagement } = useContext(IntegrationContext);
    const { dataServices, lookupData, loadLookupData: { ppVisions, entityStatuses, groups, directorates, orgLevel3s, users: { all: allUsers } } } = useContext(DataContext);

    useMemo(() => entityStatuses(), [entityStatuses]);
    useMemo(() => ppVisions(), [ppVisions]);
    useMemo(() => allUsers(), [allUsers]);
    useMemo(() => groups(), [groups]);
    useMemo(() => directorates(), [directorates]);
    useMemo(() => orgLevel3s(), [orgLevel3s]);



    const validateEntity = (objective: IPPObjective): Promise<ObjectiveValidations> => {
        const errors = new ObjectiveValidations();

        if (objective.Title === null || objective.Title === '') {
            errors.Title = `Objective is required`;
            errors.Valid = false;
        }
        else {
            errors.Title = null;
        }

        if (objective.OwnerTypeID === 1 && objective.OwnerL1ID === null) {
            errors.OwnerL1 = `Owner ${orbConfig.OrgL1Label} is required`;
            errors.Valid = false;
        }
        else {
            errors.OwnerL1 = null;
        }
        if (objective.OwnerTypeID === 2 && objective.OwnerL2ID === null) {
            errors.OwnerL2 = `Owner ${orbConfig.OrgL2Label} is required`;
            errors.Valid = false;
        }
        else {
            errors.OwnerL2 = null;
        }
        if (objective.OwnerTypeID === 3 && objective.OwnerL3ID === null) {
            errors.OwnerL3 = `Owner ${orbConfig.OrgL3Label} is required`;
            errors.Valid = false;
        }
        else {
            errors.OwnerL3 = null;
        }


        return Promise.resolve(errors);
    };


    return (
        <EntityForm<IPPObjective, ObjectiveValidations>
            {...props}
            entityName='Objective'
            renderFormFields={(changeHandlers, formState) => {
                const { FormData: objective, ValidationErrors: errors } = formState;
                return (
                    <div>
                        <CrTextField
                            label="Objective"
                            maxLength={1000}
                            className={styles.formField}
                            required={true}
                            value={objective.Title}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Title')}
                            errorMessage={errors.Title} />

                        <CrTextField
                            label="Details"
                            multiline
                            rows={6}
                            maxLength={10000}
                            className={styles.formField}
                            value={objective.Description}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Description')}
                        />

                        <CrDropdown
                            label='Vision(s)'
                            className={styles.formField}
                            multiSelect
                            options={LookupService.entitiesToSelectableOptions(lookupData.PPVisions)}
                            selectedKeys={objective.PPObjectiveVisions?.map(x => x.VisionID)}
                            onChange={(_, v) => changeHandlers.changeMultiDropdown(v, 'PPObjectiveVisions', new PPVisionObjective(), 'VisionID')}
                        />

                        <CrDropdown
                            label="Owner Type"
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions([
                                { ID: 0, Title: orbConfig.OrgTypeLabel },
                                { ID: 1, Title: orbConfig.OrgL1Label },
                                { ID: 2, Title: orbConfig.OrgL2Label },
                                { ID: 3, Title: orbConfig.OrgL3Label },
                            ])}
                            selectedKey={objective.OwnerTypeID}
                            onChange={(_, o) => changeHandlers.changeDropdown(o, 'OwnerTypeID')}
                        />
                        {objective.OwnerTypeID === 1 &&
                            <CrDropdown
                                label={`Owner ${orbConfig.OrgL1Label}`}
                                className={styles.formField}
                                options={LookupService.entitiesToSelectableOptions(lookupData.Groups)}
                                selectedKey={objective.OwnerL1ID}
                                onChange={(_, option, index) => changeHandlers.changeDropdown(option, 'OwnerL1ID', index)}
                                errorMessage={errors.OwnerL1}
                            />
                        }
                        {objective.OwnerTypeID === 2 &&
                            <CrDropdown
                                label={`Owner ${orbConfig.OrgL2Label}`}
                                className={styles.formField}
                                options={LookupService.entitiesToSelectableOptions(lookupData.Directorates)}
                                selectedKey={objective.OwnerL2ID}
                                onChange={(_, option, index) => changeHandlers.changeDropdown(option, 'OwnerL2ID', index)}
                                errorMessage={errors.OwnerL2}
                            />
                        }
                        {objective.OwnerTypeID === 3 &&
                            <CrDropdown
                                label={`Owner ${orbConfig.OrgL3Label}`}
                                className={styles.formField}
                                options={LookupService.entitiesToSelectableOptions(lookupData.OrgLevel3s)}
                                selectedKey={objective.OwnerL3ID}
                                onChange={(_, option, index) => changeHandlers.changeDropdown(option, 'OwnerL3ID', index)}
                                errorMessage={errors.OwnerL3}
                            />
                        }

                        <CrDropdown
                            label="Status"
                            className={styles.formField}
                            disabled={disableGroupManagement}
                            options={LookupService.entitiesToSelectableOptions(lookupData.EntityStatuses)}
                            selectedKey={objective.EntityStatusID}
                            onChange={(_, o) => changeHandlers.changeStatusDropdown(o, 'EntityStatusID')}
                        />

                    </div>
                );
            }}
            loadEntity={id => dataServices.ppObjectiveService.read(id, true, true)}
            loadNewEntity={() => new PPObjective()}
            loadEntityValidations={() => new ObjectiveValidations()}
            onValidateEntity={validateEntity}
            onCreate={o => dataServices.ppObjectiveService.create(o)}
            onUpdate={o => dataServices.ppObjectiveService.update(o.ID, o)}
            parentEntities={dataServices.ppObjectiveService.parentEntities}
            childEntities={[
                {
                    ObjectParentProperty: 'PPObjectiveVisions',
                    ParentIdProperty: 'ObjectiveID',
                    ChildIdProperty: 'VisionID',
                    ChildService: dataServices.ppVisionObjectiveService
                },
            ]}
        />
    );
};
